import { Auth0Client } from '@auth0/auth0-spa-js';
import {
  postAccountIntegrateExecuteWithExistingAccount,
  postAccountIntegratePreserveAuthorization,
} from '../../api/apiClient';
import type { LoginWithRedirectAppState } from '../../utils/auth/AuthProvider';
import { deleteAuthSuccessParams } from '../../utils/auth/search-params';
import type { IntegrationConflictCodeValue } from './error-codes';
import { extractConflictCode } from './error-codes';

const integrationAppAuth0Client = new Auth0Client({
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_INTEGRATION_APP_AUTH0_CLIENT_ID,
  authorizationParams: {
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
    scope: 'openid profile',
  },
});

export async function signInToIntegrationApp({
  screenHint,
  loginHint,
  callbackClientId,
}: { screenHint?: string; loginHint?: string; callbackClientId?: string } = {}): Promise<void> {
  const { response } = await postAccountIntegratePreserveAuthorization();
  if (!response.ok) {
    throw new Error('認証エラー');
  }

  const appState: LoginWithRedirectAppState = { callbackClientId };

  await integrationAppAuth0Client.loginWithRedirect<LoginWithRedirectAppState>({
    appState,
    authorizationParams: {
      redirect_uri: `${window.location.origin}/integration/callback`,
      screen_hint: screenHint,
      login_hint: loginHint,
      prompt: 'login',
    },
  });
}

/**
 * 再ログイン後のユーザと、再ログイン前に保存したユーザ情報を用いて統合を実行する。
 * 再ログイン前のユーザの情報は http only cookie のため、実際にリクエストを送ったうえで確認する
 * returnUrl を決定するための callbackClientId は AppState 経由で認証後に取得する
 * conflictCode は 409 の場合のみ、その原因を表す
 */
export async function integrateWithPreservedAuthorization(): Promise<{
  status: number;
  returnUrl?: string;
  conflictCode?: IntegrationConflictCodeValue;
}> {
  // AuthProvider の外で実行する必要がある
  const { appState } = await integrationAppAuth0Client.handleRedirectCallback<LoginWithRedirectAppState>();
  deleteAuthSuccessParams();

  const user = await integrationAppAuth0Client.getUser();

  // handleCallback が正しく認証されれば user は存在する (キャッシュではない)
  if (!user) {
    return { status: 403 };
  }

  const { data, error, response } = await postAccountIntegrateExecuteWithExistingAccount({
    callbackClientId: appState?.callbackClientId,
  });

  return {
    status: response.status,
    returnUrl: data?.returnUrl,
    conflictCode: response.status === 409 ? extractConflictCode(error) : undefined,
  };
}
